import React, { useState, useEffect, useCallback } from 'react';
import { productService, customerService, warehouseService } from '../../services/financeService';
import { buildProductRows } from '../../utils/exportUtils';
import CsvActions from '../common/CsvActions';
import './Financial.css';

const CATEGORIES = [
  { value: 'RAW_MATERIAL', label: 'Raw Material' },
  { value: 'PACKAGING', label: 'Packaging' },
  { value: 'FINISHED_GOODS', label: 'Finished Goods' },
  { value: 'CONSUMABLE', label: 'Consumable' },
  { value: 'SPARE_PART', label: 'Spare Part' },
  { value: 'BYPRODUCT', label: 'Byproduct (e.g. Bori/Katta bags)' },
  { value: 'OTHER', label: 'Other' },
];

const UNITS = ['KG', 'TON', 'BAG', 'PIECE', 'BOX', 'LITER', 'METER', 'CONTAINER'];

const PACK_SIZES = [
  '5kg Bag',
  '10kg Bag',
  '20kg Bag',
  '25kg Bag',
  '50kg Bag',
  '100kg Bag',
  '500g Pack',
  '1kg Pack',
  '1 TON',
  'Loose / Bulk',
  'Custom...',
];

const fmt = (v) => v != null ? Number(v).toLocaleString('en-US', { minimumFractionDigits: 0 }) : '—';

const Badge = ({ text, color = 'gray' }) => {
  const colors = {
    gray: { bg: 'var(--color-bg-tertiary)', text: 'var(--color-neutral-700)' },
    green: { bg: 'var(--color-success-light)', text: 'var(--color-success)' },
    blue: { bg: 'var(--color-info-light)', text: 'var(--color-primary)' },
    yellow: { bg: 'var(--color-warning-light)', text: 'var(--color-warning)' },
    red: { bg: 'var(--color-error-light)', text: 'var(--color-error)' },
  };
  const c = colors[color] || colors.gray;
  return (
    <span style={{ background: c.bg, color: c.text, padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 500 }}>
      {text}
    </span>
  );
};

const ProductManagement = () => {
  const [activeTab, setActiveTab] = useState('products');

  // Products state
  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Stock adjustment state
  const [adjustingProduct, setAdjustingProduct] = useState(null);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustSaving, setAdjustSaving] = useState(false);
  const [adjustError, setAdjustError] = useState(null);

  // Stock history state
  const [historyProduct, setHistoryProduct] = useState(null);
  const [stockHistory, setStockHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Toast notification
  const [toast, setToast] = useState(null); // { msg, type: 'success'|'error' }
  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Warehouses state
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseLoading, setWarehouseLoading] = useState(false);
  const [warehouseError, setWarehouseError] = useState(null);
  const [deleteWarehouseConfirm, setDeleteWarehouseConfirm] = useState(null); // { id, name }
  const [showWarehouseForm, setShowWarehouseForm] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [productsRes, vendorsRes] = await Promise.all([
        productService.getAll({ 
          category: categoryFilter || undefined,
          search: searchQuery || undefined,
          activeOnly: !showInactive
        }),
        customerService.getByType('VENDOR').catch(() => ({ success: true, data: [] }))
      ]);
      if (productsRes.success) setProducts(productsRes.data || []);
      if (vendorsRes.success) setVendors(vendorsRes.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, searchQuery, showInactive]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchWarehouses = useCallback(async () => {
    setWarehouseLoading(true);
    setWarehouseError(null);
    try {
      const res = await warehouseService.getAll();
      if (res.success) setWarehouses(res.data || []);
    } catch (e) { setWarehouseError(e.message); }
    finally { setWarehouseLoading(false); }
  }, []);

  useEffect(() => { if (activeTab === 'warehouses') fetchWarehouses(); }, [activeTab, fetchWarehouses]);

  const handleAdjustStock = async (e) => {
    e.preventDefault();
    if (!adjustAmount) return;
    setAdjustSaving(true);
    setAdjustError(null);
    try {
      const res = await productService.adjustStock(adjustingProduct.id, parseFloat(adjustAmount), adjustReason || 'Manual adjustment');
      if (res.success) {
        setAdjustingProduct(null);
        setAdjustAmount('');
        setAdjustReason('');
        showToast(`Stock updated for ${adjustingProduct.name}`);
        fetchData();
      } else setAdjustError(res.message || 'Failed to adjust stock');
    } catch (e) { setAdjustError(e.message); }
    finally { setAdjustSaving(false); }
  };

  const openStockHistory = async (product) => {
    setHistoryProduct(product);
    setStockHistory([]);
    setHistoryLoading(true);
    try {
      const res = await productService.getStockHistory(product.id);
      setStockHistory(res?.data || []);
    } catch (e) {
      setStockHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleToggleActive = async (id) => {
    try {
      await productService.toggleActive(id);
      fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  const openEdit = (product) => {
    setEditingProduct(product);
    setShowForm(true);
  };

  const openCreate = () => {
    setEditingProduct(null);
    setShowForm(true);
  };

  const filteredProducts = products.filter(p => {
    if (!showInactive && !p.active) return false;
    return true;
  });

  if (loading && activeTab === 'products') {
    return <div className="financial-container"><div className="loading-state">Loading products...</div></div>;
  }

  return (
    <div className="financial-container">
      <div className="financial-header">
        <div>
          <h2 className="financial-title">Product Catalog</h2>
          <p className="financial-subtitle">Products, pricing, vendors &amp; warehouses — used in Gate Passes, Invoices &amp; Costing</p>
        </div>
        {activeTab === 'products' && (
          <div style={{ display: 'flex', gap: '10px' }}>
            {(() => { const { headers, matrix } = buildProductRows(products); return (
              <CsvActions filename="product_catalog" headers={headers} matrix={matrix} />
            ); })()}
            <button className="btn-primary" onClick={openCreate}>+ New Product</button>
          </div>
        )}
        {activeTab === 'warehouses' && <button className="btn-primary" onClick={() => { setEditingWarehouse(null); setShowWarehouseForm(true); }}>+ New Warehouse</button>}
      </div>

      {/* Tabs */}
      <div className="tab-container" style={{ marginBottom: '16px' }}>
        {[{ key: 'products', label: 'Products' }, { key: 'warehouses', label: 'Warehouses' }].map(t => (
          <button key={t.key} className={`tab-button ${activeTab === t.key ? 'active' : ''}`} onClick={() => setActiveTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── PRODUCTS TAB ── */}
      {activeTab === 'products' && (
        <>
          {error && <div style={{ background: 'var(--color-error-light)', border: '1px solid var(--color-error-border)', color: 'var(--color-error)', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>{error}</div>}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px', alignItems: 'center' }}>
            <input type="text" placeholder="Search by name or code..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="form-control" style={{ maxWidth: '250px' }} />
            <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="form-control" style={{ maxWidth: '180px' }}>
              <option value="">All Categories</option>
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
              <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} /> Show inactive
            </label>
          </div>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr><th>Code</th><th>Name</th><th>Category</th><th>Pack Size</th><th>Unit</th><th>Vendor</th><th>Last Purchase</th><th>Sale Price</th><th>Stock</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {filteredProducts.length === 0 ? (
                  <tr><td colSpan="11"><div className="empty-state"><div className="empty-state-icon">—</div><h3>No products found</h3><p>Create your first product to get started</p></div></td></tr>
                ) : filteredProducts.map(product => (
                  <tr key={product.id} style={{ opacity: product.active ? 1 : 0.6 }}>
                    <td><strong>{product.productCode}</strong></td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{product.name}</div>
                      {product.description && <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product.description}</div>}
                    </td>
                    <td><Badge text={CATEGORIES.find(c => c.value === product.category)?.label || product.category} color={product.category === 'RAW_MATERIAL' ? 'yellow' : product.category === 'FINISHED_GOODS' ? 'green' : 'gray'} /></td>
                    <td>{product.packSize || '—'}</td>
                    <td>{product.unit}</td>
                    <td style={{ fontSize: '0.85rem' }}>{product.primaryVendorName || '—'}</td>
                    <td style={{ fontSize: '0.85rem' }}>{product.lastPurchasePrice ? <span>{product.lastPurchaseCurrency || 'PKR'} {fmt(product.lastPurchasePrice)}</span> : '—'}</td>
                    <td style={{ fontSize: '0.85rem' }}>{product.defaultSalePrice ? <span>{product.defaultSaleCurrency || 'PKR'} {fmt(product.defaultSalePrice)}</span> : '—'}</td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                        <span style={{ fontWeight: product.lowStock ? 600 : 400, color: product.lowStock ? 'var(--color-error)' : 'inherit' }}>
                          {product.currentStock != null ? fmt(product.currentStock) : '—'} {product.unit}
                          {product.lowStock && <span style={{ marginLeft: '4px', color: 'var(--color-error)', fontSize: '0.65rem', background: 'var(--color-error-light)', padding: '1px 4px', borderRadius: '3px' }}>LOW</span>}
                        </span>
                        <button className="btn-icon" style={{ fontSize: '11px', padding: '2px 7px' }}
                          onClick={() => { setAdjustingProduct(product); setAdjustAmount(''); setAdjustReason(''); setAdjustError(null); }}>
                          +/− Stock
                        </button>
                        <button className="btn-icon" style={{ fontSize: '11px', padding: '2px 7px' }}
                          onClick={() => openStockHistory(product)}>
                          History
                        </button>
                      </div>
                    </td>
                    <td><Badge text={product.active ? 'Active' : 'Inactive'} color={product.active ? 'green' : 'gray'} /></td>
                    <td>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button className="btn-icon" onClick={() => openEdit(product)}>Edit</button>
                        <button className="btn-icon" style={{ background: product.active ? 'var(--color-error-light)' : 'var(--color-success-light)', color: product.active ? 'var(--color-error)' : 'var(--color-success)' }} onClick={() => handleToggleActive(product.id)}>
                          {product.active ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── WAREHOUSES TAB ── */}
      {activeTab === 'warehouses' && (
        <>
          {warehouseError && <div style={{ background: 'var(--color-error-light)', border: '1px solid var(--color-error-border)', color: 'var(--color-error)', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>{warehouseError}</div>}
          {warehouseLoading ? <div className="loading-state">Loading warehouses...</div> : (
            <div className="table-container">
              <table className="data-table">
                <thead><tr><th>#</th><th>Warehouse Name</th><th>Location / Address</th><th>Description</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {warehouses.length === 0 ? (
                    <tr><td colSpan="6"><div className="empty-state"><div className="empty-state-icon">—</div><h3>No warehouses yet</h3><p>Add warehouses to use them in Gate Passes and stock tracking</p></div></td></tr>
                  ) : warehouses.map((wh, i) => (
                    <tr key={wh.id} style={{ opacity: wh.active ? 1 : 0.55 }}>
                      <td style={{ color: 'var(--color-text-tertiary)', fontSize: '0.8rem' }}>{i + 1}</td>
                      <td><strong>{wh.name}</strong></td>
                      <td style={{ fontSize: '0.85rem' }}>{wh.location || '—'}</td>
                      <td style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>{wh.description || '—'}</td>
                      <td><Badge text={wh.active ? 'Active' : 'Inactive'} color={wh.active ? 'green' : 'gray'} /></td>
                      <td>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button className="btn-icon" onClick={() => { setEditingWarehouse(wh); setShowWarehouseForm(true); }}>Edit</button>
                          <button className="btn-icon" style={{ background: wh.active ? 'var(--color-error-light)' : 'var(--color-success-light)', color: wh.active ? 'var(--color-error)' : 'var(--color-success)' }}
                            onClick={async () => { try { const r = await warehouseService.toggle(wh.id); if (r.success) fetchWarehouses(); } catch (e) { setWarehouseError(e.message); } }}>
                            {wh.active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button className="btn-icon" style={{ background: 'var(--color-error-light)', color: 'var(--color-error)' }}
                            onClick={() => setDeleteWarehouseConfirm({ id: wh.id, name: wh.name })}>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── Stock Adjustment Modal ── */}
      {adjustingProduct && (
        <div className="modal-overlay" onClick={() => setAdjustingProduct(null)}>
          <div className="modal-content" style={{ maxWidth: '400px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Adjust Stock — {adjustingProduct.name}</h3>
              <button className="modal-close" onClick={() => setAdjustingProduct(null)}>✕</button>
            </div>
            <p style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginBottom: '12px' }}>
              Current stock: <strong>{adjustingProduct.currentStock != null ? fmt(adjustingProduct.currentStock) : 0} {adjustingProduct.unit}</strong>
            </p>
            <form onSubmit={handleAdjustStock}>
              {adjustError && <div style={{ background: 'var(--color-error-light)', border: '1px solid var(--color-error-border)', color: 'var(--color-error)', padding: '10px', borderRadius: '6px', marginBottom: '12px', fontSize: '0.85rem' }}>{adjustError}</div>}
              <div className="form-group">
                <label className="form-label">Amount *</label>
                <input type="number" className="form-control" step="0.001" placeholder="e.g. +500 to receive, -50 to dispatch" value={adjustAmount} onChange={e => setAdjustAmount(e.target.value)} required autoFocus />
                <small style={{ color: 'var(--color-text-secondary)', fontSize: '0.78rem' }}>Positive = receive stock &nbsp;|&nbsp; Negative = dispatch / consume</small>
              </div>
              <div className="form-group">
                <label className="form-label">Reason</label>
                <input className="form-control" placeholder="e.g. Received from supplier, Dispatched, Damaged" value={adjustReason} onChange={e => setAdjustReason(e.target.value)} />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setAdjustingProduct(null)} disabled={adjustSaving}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={adjustSaving || !adjustAmount}>{adjustSaving ? 'Saving...' : 'Apply'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Stock History Modal ── */}
      {historyProduct && (() => {
        const fmtQty = (q) => Math.abs(Number(q)).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const fmtMoney = (v, cur) => v != null ? `${cur || 'PKR'} ${Number(v).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';
        const totalIn  = stockHistory.filter(m => m.movementType === 'INWARD' || m.movementType === 'ADJUSTMENT_IN' || m.movementType === 'OPENING').reduce((s, m) => s + Math.abs(Number(m.quantity)), 0);
        const totalOut = stockHistory.filter(m => m.movementType === 'OUTWARD' || m.movementType === 'ADJUSTMENT_OUT').reduce((s, m) => s + Math.abs(Number(m.quantity)), 0);
        const totalCostIn  = stockHistory.filter(m => m.movementType === 'INWARD').reduce((s, m) => s + (m.totalValue ? Number(m.totalValue) : 0), 0);
        const totalRevOut  = stockHistory.filter(m => m.movementType === 'OUTWARD').reduce((s, m) => s + (m.totalValue ? Number(m.totalValue) : 0), 0);

        const TYPE_META = {
          INWARD:         { label: 'Stock In',    icon: '⬆', bg: 'var(--color-success-light)', color: 'var(--color-success)', from: 'Supplier' },
          OUTWARD:        { label: 'Stock Out',   icon: '⬇', bg: 'var(--color-error-light)', color: 'var(--color-error)', from: 'Customer' },
          ADJUSTMENT_IN:  { label: 'Adj. In',     icon: '+', bg: 'var(--color-info-light)', color: 'var(--color-info)', from: '' },
          ADJUSTMENT_OUT: { label: 'Adj. Out',    icon: '−', bg: 'var(--color-warning-light)', color: 'var(--color-warning)', from: '' },
          OPENING:        { label: 'Opening',     icon: '○', bg: 'var(--color-bg-tertiary)', color: 'var(--color-neutral-700)', from: '' },
        };

        const SOURCE_LABEL = {
          GATE_PASS:       'Gate Pass',
          PURCHASE:        'Purchase',
          PURCHASE_CANCEL: 'PO Cancel',
          INVOICE:         'Invoice',
          WASTAGE:         'Wastage',
          MANUAL_ADJUSTMENT: 'Manual',
        };

        return (
          <div className="modal-overlay" onClick={() => setHistoryProduct(null)}>
            <div className="modal-content" style={{ maxWidth: '960px', padding: 0 }} onClick={e => e.stopPropagation()}>

              {/* Header */}
              <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--color-border-medium)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 700 }}>
                      Stock Ledger — {historyProduct.name}
                      <span style={{ fontSize: '11px', fontWeight: 400, color: 'var(--color-text-secondary)', marginLeft: '10px' }}>[{historyProduct.productCode}]</span>
                    </h3>
                    <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                      Complete history of every stock change for this product
                    </p>
                  </div>
                  <button className="modal-close" onClick={() => setHistoryProduct(null)}>✕</button>
                </div>

                {/* Summary bar */}
                <div style={{ display: 'flex', gap: '12px', marginTop: '14px', flexWrap: 'wrap' }}>
                  {[
                    { label: 'Current Stock', value: `${Number(historyProduct.currentStock ?? 0).toLocaleString()} ${historyProduct.unit}`, bg: 'var(--color-success-light)', color: 'var(--color-success)', border: 'var(--color-success-border)' },
                    { label: 'Total Received', value: `${fmtQty(totalIn)} ${historyProduct.unit}`, bg: 'var(--color-info-light)', color: 'var(--color-info)', border: 'var(--color-info)' },
                    { label: 'Total Dispatched', value: `${fmtQty(totalOut)} ${historyProduct.unit}`, bg: 'var(--color-warning-light)', color: 'var(--color-warning-strong)', border: 'var(--color-warning-border)' },
                    { label: 'Total Purchase Cost', value: fmtMoney(totalCostIn, 'PKR'), bg: 'var(--color-accent-light)', color: 'var(--color-accent-hover)', border: 'var(--color-accent-light)' },
                    { label: 'Total Sale Value', value: fmtMoney(totalRevOut, 'PKR'), bg: 'var(--color-error-light)', color: 'var(--color-error)', border: 'var(--color-error-border)' },
                  ].map(s => (
                    <div key={s.label} style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: '8px', padding: '8px 14px', minWidth: '140px' }}>
                      <div style={{ fontSize: '10px', color: 'var(--color-text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: s.color, marginTop: '2px' }}>{s.value}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Table */}
              <div style={{ padding: '0', overflowX: 'auto', maxHeight: '480px', overflowY: 'auto' }}>
                {historyLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-text-tertiary)' }}>Loading ledger...</div>
                ) : stockHistory.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-text-tertiary)' }}>
                    <div className="empty-state-icon">—</div>
                    No stock movements recorded yet.
                    <div style={{ fontSize: '12px', marginTop: '6px' }}>
                      Use Gate Pass Receive/Dispatch, Purchases, Invoices, or manual adjustment to record movements.
                    </div>
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ background: 'var(--color-neutral-50)', position: 'sticky', top: 0, zIndex: 1 }}>
                        <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--color-neutral-700)', borderBottom: '1px solid var(--color-border-medium)', whiteSpace: 'nowrap' }}>Date & Time</th>
                        <th style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 600, color: 'var(--color-neutral-700)', borderBottom: '1px solid var(--color-border-medium)' }}>Type</th>
                        <th style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 600, color: 'var(--color-neutral-700)', borderBottom: '1px solid var(--color-border-medium)' }}>Source</th>
                        <th style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 600, color: 'var(--color-success)', borderBottom: '1px solid var(--color-border-medium)' }}>IN</th>
                        <th style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 600, color: 'var(--color-error)', borderBottom: '1px solid var(--color-border-medium)' }}>OUT</th>
                        <th style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 600, color: 'var(--color-neutral-700)', borderBottom: '1px solid var(--color-border-medium)' }}>Balance</th>
                        <th style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 600, color: 'var(--color-accent-hover)', borderBottom: '1px solid var(--color-border-medium)' }}>Unit Price</th>
                        <th style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 600, color: 'var(--color-accent-hover)', borderBottom: '1px solid var(--color-border-medium)' }}>Total Value</th>
                        <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--color-neutral-700)', borderBottom: '1px solid var(--color-border-medium)' }}>Reference</th>
                        <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: 600, color: 'var(--color-neutral-700)', borderBottom: '1px solid var(--color-border-medium)' }}>Party / Warehouse</th>
                        <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: 600, color: 'var(--color-neutral-700)', borderBottom: '1px solid var(--color-border-medium)' }}>Recorded By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stockHistory.map((m, idx) => {
                        const isIn = m.movementType === 'INWARD' || m.movementType === 'ADJUSTMENT_IN' || m.movementType === 'OPENING';
                        const meta = TYPE_META[m.movementType] || { label: m.movementType, icon: '•', bg: 'var(--color-bg-tertiary)', color: 'var(--color-neutral-700)' };
                        const srcLabel = SOURCE_LABEL[m.referenceType] || m.referenceType || '';

                        return (
                          <tr key={m.id} style={{ background: idx % 2 === 0 ? '#fff' : 'var(--color-neutral-50)', borderBottom: '1px solid var(--color-bg-tertiary)' }}>
                            <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: 'var(--color-neutral-700)' }}>
                              {m.createdAt
                                ? new Date(m.createdAt).toLocaleString('en-PK', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })
                                : '—'}
                            </td>
                            <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                              <span style={{ background: meta.bg, color: meta.color, padding: '3px 8px', borderRadius: '12px', fontWeight: 600, fontSize: '11px', whiteSpace: 'nowrap' }}>
                                {meta.icon} {meta.label}
                              </span>
                            </td>
                            <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                              {srcLabel && (
                                <span style={{ background: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)', padding: '2px 7px', borderRadius: '4px', fontSize: '10px', fontWeight: 600 }}>
                                  {srcLabel}
                                </span>
                              )}
                            </td>
                            <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 700, color: 'var(--color-success)', fontSize: '13px' }}>
                              {isIn ? `+${fmtQty(m.quantity)} ${historyProduct.unit}` : ''}
                            </td>
                            <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 700, color: 'var(--color-error)', fontSize: '13px' }}>
                              {!isIn ? `−${fmtQty(m.quantity)} ${historyProduct.unit}` : ''}
                            </td>
                            <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 700, color: 'var(--color-info-strong)', fontSize: '13px', background: 'var(--color-info-light)' }}>
                              {Number(m.balanceAfter).toLocaleString('en-PK', { minimumFractionDigits: 2 })} {historyProduct.unit}
                            </td>
                            <td style={{ padding: '10px 8px', textAlign: 'right', color: 'var(--color-accent-hover)', fontWeight: 600 }}>
                              {m.unitPrice != null
                                ? <>{m.priceCurrency || 'PKR'} {Number(m.unitPrice).toLocaleString('en-PK', { minimumFractionDigits: 2 })}<span style={{ fontSize: '10px', color: 'var(--color-text-tertiary)' }}>/{historyProduct.unit}</span></>
                                : <span style={{ color: 'var(--color-border-strong)' }}>—</span>}
                            </td>
                            <td style={{ padding: '10px 8px', textAlign: 'right', color: 'var(--color-accent-hover)', fontWeight: 600 }}>
                              {m.totalValue != null
                                ? `${m.priceCurrency || 'PKR'} ${Number(m.totalValue).toLocaleString('en-PK', { minimumFractionDigits: 0 })}`
                                : <span style={{ color: 'var(--color-border-strong)' }}>—</span>}
                            </td>
                            <td style={{ padding: '10px 14px' }}>
                              <span style={{ color: 'var(--color-primary)', fontWeight: 600, fontSize: '11px' }}>{m.referenceNumber || '—'}</span>
                              {m.notes && (
                                <div style={{ fontSize: '10px', color: 'var(--color-text-tertiary)', marginTop: '2px', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={m.notes}>
                                  {m.notes}
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '10px 8px', fontSize: '11px', color: 'var(--color-neutral-700)' }}>
                              {m.partyName && <div style={{ fontWeight: 600 }}>{m.partyName}</div>}
                              {m.warehouseName && <div style={{ color: 'var(--color-text-tertiary)' }}>{m.warehouseName}</div>}
                              {!m.partyName && !m.warehouseName && <span style={{ color: 'var(--color-border-strong)' }}>—</span>}
                            </td>
                            <td style={{ padding: '10px 8px', fontSize: '11px', color: 'var(--color-text-secondary)' }}>
                              {m.createdByName || '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              <div style={{ padding: '14px 24px', borderTop: '1px solid var(--color-border-medium)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>{stockHistory.length} movement{stockHistory.length !== 1 ? 's' : ''} recorded</span>
                <button className="btn-secondary" onClick={() => setHistoryProduct(null)}>Close</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Toast Notification ── */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999,
          background: toast.type === 'error' ? 'var(--color-error-light)' : 'var(--color-success-light)',
          color: toast.type === 'error' ? 'var(--color-error)' : 'var(--color-success)',
          border: `1px solid ${toast.type === 'error' ? 'var(--color-error-border)' : 'var(--color-success-border)'}`,
          padding: '14px 22px', borderRadius: '10px', fontSize: '14px', fontWeight: 600,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          display: 'flex', alignItems: 'center', gap: '10px',
          animation: 'slideIn 0.3s ease',
        }}>
          <span>{toast.type === 'error' ? '!' : '✓'}</span>
          {toast.msg}
        </div>
      )}

      {/* ── Delete Warehouse Confirm ── */}
      {deleteWarehouseConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteWarehouseConfirm(null)}>
          <div className="modal-content" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Warehouse</h3>
              <button className="modal-close" onClick={() => setDeleteWarehouseConfirm(null)}>✕</button>
            </div>
            <p style={{ padding: '12px 0', color: 'var(--color-neutral-700)' }}>Delete <strong>{deleteWarehouseConfirm.name}</strong>? This cannot be undone.</p>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setDeleteWarehouseConfirm(null)}>Cancel</button>
              <button className="btn-danger" onClick={async () => {
                const { id } = deleteWarehouseConfirm;
                setDeleteWarehouseConfirm(null);
                try { await warehouseService.delete(id); fetchWarehouses(); }
                catch (e) { setWarehouseError(e.message); }
              }}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Warehouse Form Modal ── */}
      {showWarehouseForm && (
        <WarehouseForm
          warehouse={editingWarehouse}
          onClose={() => { setShowWarehouseForm(false); setEditingWarehouse(null); }}
          onSave={() => { setShowWarehouseForm(false); setEditingWarehouse(null); fetchWarehouses(); }}
        />
      )}

      {/* ── Product Form Modal ── */}
      {showForm && (
        <ProductForm
          product={editingProduct}
          vendors={vendors}
          onClose={() => { setShowForm(false); setEditingProduct(null); }}
          onSave={() => { setShowForm(false); setEditingProduct(null); fetchData(); }}
        />
      )}
    </div>
  );
};

export const ProductForm = ({ product, vendors, excludeCategories = [], onClose, onSave }) => {
  const availableCategories = CATEGORIES.filter(c => !excludeCategories.includes(c.value));
  const existingPackSize = product?.packSize || '';
  const isCustom = existingPackSize && !PACK_SIZES.includes(existingPackSize);

  const [formData, setFormData] = useState({
    productCode: product?.productCode || '',
    name: product?.name || '',
    description: product?.description || '',
    category: product?.category || 'FINISHED_GOODS',
    packSize: isCustom ? 'Custom...' : existingPackSize,
    unit: product?.unit || 'KG',
    primaryVendorId: product?.primaryVendorId || '',
    lastPurchasePrice: product?.lastPurchasePrice || '',
    lastPurchaseCurrency: product?.lastPurchaseCurrency || 'PKR',
    defaultSalePrice: product?.defaultSalePrice || '',
    defaultSaleCurrency: product?.defaultSaleCurrency || 'PKR',
    reorderLevel: product?.reorderLevel || '',
    currentStock: product?.currentStock || '',
    inventreePartId: product?.inventreePartId || '',
  });
  const [customPackSize, setCustomPackSize] = useState(isCustom ? existingPackSize : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Auto-fill product code when name is entered (only for new products)
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Byproduct bags (Katta/Bori) are counted, not weighed — "50 Katta bags", not "50 KG of
    // Katta". Nudge new products toward BAG when Category switches to Byproduct, but only if
    // Unit is still at its default KG — don't clobber a value the user already deliberately set.
    if (name === 'category' && value === 'BYPRODUCT' && !product && formData.unit === 'KG') {
      setFormData(prev => ({ ...prev, category: value, unit: 'BAG' }));
    }
    if (name === 'name' && !product) {
      // Debounce: generate code preview client-side from name
      const prefix = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().substring(0, 4) || 'PROD';
      setFormData(prev => ({ ...prev, name: value, productCode: prefix + '-???' }));
      // Fetch real next code from server after short delay
      clearTimeout(window._productCodeTimer);
      window._productCodeTimer = setTimeout(async () => {
        try {
          const code = await productService.suggestCode(value);
          if (code) setFormData(prev => ({ ...prev, productCode: code }));
        } catch {}
      }, 400);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      const resolvedPackSize = formData.packSize === 'Custom...' ? customPackSize.trim() : formData.packSize;
      const payload = {
        ...formData,
        packSize: resolvedPackSize || null,
        primaryVendorId: formData.primaryVendorId ? parseInt(formData.primaryVendorId) : null,
        lastPurchasePrice: formData.lastPurchasePrice ? parseFloat(formData.lastPurchasePrice) : null,
        defaultSalePrice: formData.defaultSalePrice ? parseFloat(formData.defaultSalePrice) : null,
        reorderLevel: formData.reorderLevel ? parseFloat(formData.reorderLevel) : null,
        currentStock: formData.currentStock ? parseFloat(formData.currentStock) : null,
        inventreePartId: formData.inventreePartId ? parseInt(formData.inventreePartId) : null,
      };

      const res = product
        ? await productService.update(product.id, payload)
        : await productService.create(payload);
      onSave(res?.data || res);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{product ? 'Edit Product' : 'New Product'}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          {error && (
            <div style={{ background: 'var(--color-error-light)', border: '1px solid var(--color-error-border)', color: 'var(--color-error)', padding: '10px', borderRadius: '6px', marginBottom: '16px', fontSize: '0.9rem' }}>
              {error}
            </div>
          )}

          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">Product Code <span style={{fontWeight:400,color:'var(--color-text-tertiary)'}}>(auto if blank)</span></label>
              <input
                name="productCode"
                className="form-control"
                value={formData.productCode}
                onChange={handleChange}
                placeholder="Auto-generated from name (e.g. RICE-001)"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Name *</label>
              <input
                name="name"
                className="form-control"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g. Basmati Rice Premium"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Category *</label>
              <select name="category" className="form-control" value={formData.category} onChange={handleChange}>
                {availableCategories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Unit *</label>
              <select name="unit" className="form-control" value={formData.unit} onChange={handleChange}>
                {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Pack Size</label>
              <select
                name="packSize"
                className="form-control"
                value={formData.packSize}
                onChange={handleChange}
              >
                <option value="">— Select Pack Size —</option>
                {PACK_SIZES.map(ps => <option key={ps} value={ps}>{ps}</option>)}
              </select>
              {formData.packSize === 'Custom...' && (
                <input
                  className="form-control"
                  style={{ marginTop: '6px' }}
                  placeholder="Enter custom pack size..."
                  value={customPackSize}
                  onChange={e => setCustomPackSize(e.target.value)}
                />
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Primary Vendor</label>
              <select name="primaryVendorId" className="form-control" value={formData.primaryVendorId} onChange={handleChange}>
                <option value="">— Select Vendor —</option>
                {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              name="description"
              className="form-control"
              value={formData.description}
              onChange={handleChange}
              rows={2}
              placeholder="Product description..."
            />
          </div>

          <div style={{ borderTop: '1px solid var(--color-border-medium)', margin: '20px 0', paddingTop: '16px' }}>
            <h4 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--color-neutral-700)', marginBottom: '12px' }}>Pricing</h4>
            <div className="form-grid-2">
              <div className="form-group">
                <label className="form-label">Last Purchase Price</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select name="lastPurchaseCurrency" className="form-control" style={{ width: '80px' }} value={formData.lastPurchaseCurrency} onChange={handleChange}>
                    <option value="PKR">PKR</option>
                    <option value="USD">USD</option>
                  </select>
                  <input
                    name="lastPurchasePrice"
                    type="number"
                    className="form-control"
                    value={formData.lastPurchasePrice}
                    onChange={handleChange}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Default Sale Price</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select name="defaultSaleCurrency" className="form-control" style={{ width: '80px' }} value={formData.defaultSaleCurrency} onChange={handleChange}>
                    <option value="PKR">PKR</option>
                    <option value="USD">USD</option>
                  </select>
                  <input
                    name="defaultSalePrice"
                    type="number"
                    className="form-control"
                    value={formData.defaultSalePrice}
                    onChange={handleChange}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                </div>
              </div>
            </div>
          </div>

          <div style={{ borderTop: '1px solid var(--color-border-medium)', margin: '20px 0', paddingTop: '16px' }}>
            <h4 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--color-neutral-700)', marginBottom: '12px' }}>Inventory</h4>
            <div className="form-grid-2">
              <div className="form-group">
                <label className="form-label">Current Stock</label>
                <input
                  name="currentStock"
                  type="number"
                  className="form-control"
                  value={formData.currentStock}
                  onChange={handleChange}
                  placeholder="0"
                  step="0.001"
                  min="0"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Reorder Level</label>
                <input
                  name="reorderLevel"
                  type="number"
                  className="form-control"
                  value={formData.reorderLevel}
                  onChange={handleChange}
                  placeholder="When to reorder"
                  step="0.001"
                  min="0"
                />
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving...' : (product ? 'Update Product' : 'Create Product')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const WarehouseForm = ({ warehouse, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: warehouse?.name || '',
    location: warehouse?.location || '',
    description: warehouse?.description || '',
    active: warehouse?.active !== undefined ? warehouse.active : true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { setError('Warehouse name is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const res = warehouse
        ? await warehouseService.update(warehouse.id, formData)
        : await warehouseService.create(formData);
      if (res.success) onSave();
      else setError(res.message || 'Failed to save warehouse');
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: '460px' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{warehouse ? 'Edit Warehouse' : 'New Warehouse'}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div style={{ background: 'var(--color-error-light)', border: '1px solid var(--color-error-border)', color: 'var(--color-error)', padding: '10px', borderRadius: '6px', marginBottom: '14px', fontSize: '0.875rem' }}>{error}</div>}
          <div className="form-group">
            <label className="form-label">Warehouse Name *</label>
            <input className="form-control" value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Main Warehouse, Karachi Cold Storage" required />
          </div>
          <div className="form-group">
            <label className="form-label">Location / Address</label>
            <input className="form-control" value={formData.location} onChange={e => setFormData(p => ({ ...p, location: e.target.value }))} placeholder="e.g. Plot 12, SITE, Karachi" />
          </div>
          <div className="form-group">
            <label className="form-label">Description / Notes</label>
            <textarea className="form-control" rows={2} value={formData.description} onChange={e => setFormData(p => ({ ...p, description: e.target.value }))} placeholder="Optional notes about this warehouse" />
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving...' : (warehouse ? 'Update' : 'Create Warehouse')}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductManagement;
